import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { cn } from "@/shared/lib/cn";

interface ToastState {
  id: number;
  message: string;
  /** True during the exit animation, just before unmount. */
  leaving: boolean;
}

interface ToastContextValue {
  /** Show a brief, auto-dismissing confirmation. Replaces any visible toast. */
  toast: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const VISIBLE_MS = 2200;
const EXIT_MS = 180;

/**
 * App-level toast host: one transient pill in the bottom-right corner, used for
 * quick confirmations (e.g. "Copied"). It animates in and back out. Mounted once
 * at the composition root; consumers fire toasts through `useToast()`.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<ToastState | null>(null);
  const idRef = useRef(0);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };
  useEffect(() => clearTimers, []);

  const toast = useCallback((message: string) => {
    clearTimers();
    const id = ++idRef.current;
    setCurrent({ id, message, leaving: false });
    timers.current.push(
      setTimeout(
        () => setCurrent((c) => (c && c.id === id ? { ...c, leaving: true } : c)),
        VISIBLE_MS,
      ),
    );
    timers.current.push(
      setTimeout(() => setCurrent((c) => (c && c.id === id ? null : c)), VISIBLE_MS + EXIT_MS),
    );
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className="pointer-events-none fixed bottom-5 right-5 z-[100]">
        {current ? (
          <div
            key={current.id}
            role="status"
            aria-live="polite"
            className={cn(
              "rounded-pill bg-ink px-4 py-2 font-sans text-[13px] font-medium text-paper shadow-[var(--shadow-pill)]",
              current.leaving
                ? "[animation:toast-fall_180ms_var(--ease-house)_forwards]"
                : "[animation:toast-rise_200ms_var(--ease-house)]",
            )}
          >
            {current.message}
          </div>
        ) : null}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}
